import { resolveFieldTranslation } from "./formTranslations.js";
import { formatDate as formatStoredDate } from "./safeDate.js";
import { buildElementOptionLabelResolver } from "./masterHelper.js";
import { formatWithThousandSeparators, stripThousandSeparators } from "./numberFormatter.js";
import { rasterizeElementToPdfFile } from "./formPrint.js";

// Builds a submission "print" as a clean, structured document — section headings
// with Label / Value rows and real tables — rendered from the form definition and
// its values, rather than by photographing the live antd form. No inputs means no
// clipped text, no truncated pills and no empty boxes.

export const escapeHtml = (value) =>
    String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

const isBlank = (value) =>
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0);

// Section containers in the form tree go by either name depending on where they
// were created.
const isSection = (item) => item?.formType === "section" || item?.formType === "sectionForm";

// Decorative widgets that carry no submitted value and don't belong in a record.
const SKIP_TYPES = new Set(["divider", "infoForm", "imageDisplayForm", "buttonForm"]);

const OPTION_TYPES = new Set([
    "dropdownField", "dropdownForm", "selectListForm",
    "radioField", "radioForm",
    "checkboxField", "checkboxForm",
    "multipleChoiceField", "multipleChoiceForm",
]);

// Maps a table column's inputType to the option-lookup / formatting behaviour used
// for the equivalent standalone widget.
const columnLooksLikeOptions = (inputType) =>
    ["select", "dropdown", "radio", "checkbox", "mcq"].includes(inputType);

// A master backed widget (or table column) stores canonical ids: the value is the
// connected master's row uuid, and the option baked onto the field can hold another
// id as its label rather than display text. Resolving against the master list the
// way the live widget does keeps the record showing names instead of uuids. One
// resolver per element, since building its label maps walks every master.
// Without a master list - public forms never load one - the baked label stands, so
// the printed text matches what that form showed on screen.
const createOptionTextResolver = (masterList, lng) => {
    const resolvers = new Map();
    return (element, options, value) => {
        const match = (options || []).find((opt) => String(opt?.value ?? opt) === String(value));
        if (!resolvers.has(element)) {
            resolvers.set(element, buildElementOptionLabelResolver(element, masterList, lng));
        }
        const label = resolvers.get(element)(match ?? { value, label: value });
        return isBlank(label) ? value : label;
    };
};

// A number is stored raw ("1234.5678") and made readable by the widget as it is
// shown: grouped in thousands, cut to the decimals the field allows, and followed
// by its unit. The record has to do the same, or it reads as a different number
// from the form it was filled in on.
//
// `config` is the widget or the table column; a column carries neither a decimal
// limit nor a unit, so those two simply do not apply there.
const formatNumberValue = (value, config = {}) => {
    const raw = stripThousandSeparators(value);
    // Anything that is not a plain decimal (an exponent, a stray unit already in the
    // value) is left exactly as stored rather than rewritten into something else.
    if (!/^-?\d+(\.\d+)?$/.test(raw)) return String(value);

    // Matches the widget: 0 and "" mean "no limit", not "no decimals". The digits
    // past the limit are cut rather than rounded, which is what the field does to
    // them as they are typed - and a computed value was already rounded to this
    // same precision before it was stored.
    const { allowedDecimalPoint, unit } = config;
    const precision = (allowedDecimalPoint !== undefined && allowedDecimalPoint !== "" && Number(allowedDecimalPoint) !== 0)
        ? Number(allowedDecimalPoint)
        : undefined;

    let text = raw;
    if (precision !== undefined && text.includes(".")) {
        const [whole, decimals] = text.split(".");
        const kept = decimals.slice(0, precision);
        text = kept ? `${whole}.${kept}` : whole;
    }

    const grouped = formatWithThousandSeparators(text);
    return unit ? `${grouped} ${unit}` : grouped;
};

// A stored date is any of: a live dayjs off the picker, one that lost its methods
// on the way through redux, an ISO string from the API, a Date from an integration,
// or text in the field's own format. safeDate is the one place that knows them all,
// including parsing "01-10-2026" as the DD-MM-YYYY the field wrote rather than as
// an American month-first date.
//
// Hand-rolling this printed today's date for every field: an unwrapped dayjs object
// reaching moment() is read as a set of date *parts*, and a dayjs carries none of
// the keys moment looks for, so it filled in all of them from the clock.
const formatDate = (value, format) => {
    const pattern = format || "DD-MM-YYYY";
    const text = formatStoredDate(value, pattern, "");
    if (text) return text;
    // A value that is not a date at all is still worth showing as it stands, but an
    // object would only ever print as "[object Object]" - that reads as empty.
    return value && typeof value === "object" ? "" : String(value ?? "");
};

// Turns a user/role/group/userPicker selection into a readable, comma-joined list.
const formatUserValue = (value) => {
    if (!value || typeof value !== "object") return String(value ?? "");
    const parts = [
        ...(value.user || []),
        ...(value.role || []),
        ...(value.group || []),
        ...(value.userPicker || []),
    ];
    return parts.filter(Boolean).join(", ");
};

const formatAttachmentValue = (value) => {
    if (typeof value === "string") return value;
    if (Array.isArray(value)) return value.map((f) => f?.name || f).filter(Boolean).join(", ");
    if (value?.fileList) return value.fileList.map((f) => f?.name).filter(Boolean).join(", ");
    if (value?.file) return value.file.name || "";
    return "";
};

const formatLinkValue = (value) => {
    const links = Array.isArray(value) ? value : [value];
    return links
        .filter(Boolean)
        .map((link) => {
            if (typeof link === "string") return link;
            if (link.label && link.url) return `${link.label} (${link.url})`;
            return link.label || link.url || "";
        })
        .filter(Boolean)
        .join(", ");
};

// Resolves a single widget's stored value to display text. Tables are handled
// separately (they render as a sub-table, not a text value).
const formatWidgetValue = (widget, translated, value, optionText) => {
    if (isBlank(value)) return "";

    const type = widget.formType;

    // A master connected widget resolves through the same path whatever its type:
    // its stored value is a row uuid either way.
    if (OPTION_TYPES.has(type) || widget.parentTable) {
        const options = translated.options || widget.options || [];
        if (Array.isArray(value)) return value.map((v) => optionText(widget, options, v)).join(", ");
        return String(optionText(widget, options, value));
    }
    if (type === "numberForm") return formatNumberValue(value, widget);
    if (type === "dateForm") return formatDate(value, widget.dateFormat);
    if (type === "timeForm") return formatDate(value, widget.timeFormat || "HH:mm");
    if (type === "userForm") return formatUserValue(value);
    if (type === "linkField" || type === "linkForm") return formatLinkValue(value);
    if (type === "attachment" || type === "imageForm" || type === "attachmentForm") {
        return formatAttachmentValue(value);
    }
    if (type === "contactField" || type === "contactForm") {
        if (value && typeof value === "object") {
            return [value.code, value.number].filter(Boolean).join(" ") || JSON.stringify(value);
        }
        return String(value);
    }

    // Booleans (toggle-style widgets) and everything else fall through to text.
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value)) return value.filter((v) => !isBlank(v)).join(", ");
    if (value && typeof value === "object") return JSON.stringify(value);
    return String(value);
};

const formatCellValue = (column, value, optionText) => {
    if (isBlank(value)) return "";
    const inputType = column.inputType;
    if (columnLooksLikeOptions(inputType) || column.parentTable) {
        const options = column.options || [];
        if (Array.isArray(value)) return value.map((v) => optionText(column, options, v)).join(", ");
        return String(optionText(column, options, value));
    }
    if (inputType === "number") return formatNumberValue(value, column);
    if (inputType === "date") return formatDate(value, column.dateFormat);
    if (inputType === "time") return formatDate(value, column.timeFormat || "HH:mm");
    if (inputType === "link") return formatLinkValue(value);
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value)) return value.filter((v) => !isBlank(v)).join(", ");
    if (value && typeof value === "object") return JSON.stringify(value);
    return String(value);
};

const EMPTY_VALUE_HTML = `<span style="color:#c7ccd1;">—</span>`;

// The label column is fixed at a quarter of the row and never grows: a long label
// wraps onto further lines inside it rather than pushing the values further right,
// so every value on the page starts at the same place. `min-width:0` is what lets
// the value column shrink instead of forcing the row wider than the page.
const rowHtml = (label, valueHtml) => `
    <div data-print-break style="display:flex;align-items:flex-start;padding:8px 0;border-bottom:1px solid #f1f2f4;">
        <div style="box-sizing:border-box;flex:0 0 25%;max-width:25%;padding-right:12px;font-size:13px;color:#6b7280;line-height:1.5;word-break:break-word;overflow-wrap:anywhere;">${escapeHtml(label)}</div>
        <div style="box-sizing:border-box;flex:1 1 auto;min-width:0;font-size:14px;color:#111827;font-weight:500;line-height:1.5;white-space:pre-wrap;word-break:break-word;">${valueHtml}</div>
    </div>`;

const tableHtml = (widget, translated, value, optionText) => {
    const columns = translated.columns || widget.columns || [];
    const rows = Array.isArray(value) ? value : [];
    if (columns.length === 0) return "";

    const head = columns
        .map((col) => `<th style="text-align:left;padding:6px 10px;font-size:12px;color:#6b7280;font-weight:600;border:1px solid #e5e7eb;background:#f9fafb;">${escapeHtml(col.label || col.actualLabel || "")}</th>`)
        .join("");

    const body = rows.length === 0
        ? `<tr><td colspan="${columns.length}" style="padding:8px 10px;font-size:13px;color:#c7ccd1;border:1px solid #e5e7eb;">No rows</td></tr>`
        : rows
            .map((row) => {
                const cells = columns
                    .map((col) => {
                        const cell = formatCellValue(col, row?.[col.id], optionText);
                        const content = cell === "" ? EMPTY_VALUE_HTML : escapeHtml(cell);
                        return `<td style="padding:6px 10px;font-size:13px;color:#111827;border:1px solid #e5e7eb;vertical-align:top;word-break:break-word;">${content}</td>`;
                    })
                    .join("");
                return `<tr data-print-break>${cells}</tr>`;
            })
            .join("");

    return `
        <div data-print-break style="padding:8px 0;">
            <div class="print-table-scroll" style="overflow-x:auto;">
                <table style="border-collapse:collapse;width:100%;table-layout:fixed;">
                    <thead><tr>${head}</tr></thead>
                    <tbody>${body}</tbody>
                </table>
            </div>
        </div>`;
};

// Renders one widget (label row, or table block). Returns "" for widgets that are
// hidden by conditions or are decorative.
const widgetHtml = (widget, context) => {
    const { values, lng, visibleIds, optionText } = context;
    if (!widget || isSection(widget)) return "";
    if (SKIP_TYPES.has(widget.formType)) return "";
    // Respect the same visibility the submitter saw. When no visibility set is
    // supplied, fall through and render everything.
    if (visibleIds && !visibleIds.has(widget.id)) return "";

    const translated = resolveFieldTranslation(widget, lng) || widget;
    const label = translated.label || translated.formLabel || widget.label || "";
    const value = values?.[widget.id];

    if (widget.formType === "tableForm") {
        const rendered = tableHtml(widget, translated, value, optionText);
        if (!rendered) return "";
        return `
            <div data-print-break style="padding:8px 0;">
                <div style="font-size:13px;color:#6b7280;margin-bottom:4px;">${escapeHtml(label)}</div>
                ${rendered}
            </div>`;
    }

    const text = formatWidgetValue(widget, translated, value, optionText);
    const valueHtml = text === "" ? EMPTY_VALUE_HTML : escapeHtml(text);
    return rowHtml(label, valueHtml);
};

// Walks the form tree, emitting a section block only when it actually contains
// something visible, so empty sections don't leave stray headings.
const sectionHtml = (section, context, depth) => {
    const inner = childrenHtml(section.children || [], context, depth + 1);
    if (!inner.trim()) return "";
    const translated = resolveFieldTranslation(section, context.lng) || section;
    const label = translated.label || section.label || "";
    return `
        <div data-print-break style="margin-top:${depth === 0 ? 26 : 16}px;">
            <div style="font-size:${depth === 0 ? 16 : 14}px;font-weight:700;color:#111827;padding-bottom:6px;border-bottom:2px solid #e5e7eb;margin-bottom:6px;">${escapeHtml(label)}</div>
            ${inner}
        </div>`;
};

const childrenHtml = (items, context, depth) =>
    (items || [])
        .filter(Boolean)
        .map((item) =>
            isSection(item)
                ? sectionHtml(item, context, depth)
                : widgetHtml(item, context)
        )
        .join("");

// Everything the body needs to render one form: its values, the reader's
// language, which widgets were visible, and the master-aware option resolver.
const printContext = ({ values, lng, visibleIds, masterList }) => ({
    values,
    lng,
    visibleIds,
    optionText: createOptionTextResolver(masterList, lng),
});

// Every piece of text here arrives translated: this module has no i18n of its
// own, and "Submitted by <name>" is one phrase rather than a label plus a name,
// because the two sit in the opposite order in Japanese, Chinese and Korean.
const headerHtml = (meta = {}) => {
    const title = escapeHtml(meta.title || "Form Submission");
    const bits = [];
    if (meta.submittedByText) bits.push(escapeHtml(meta.submittedByText));
    if (meta.stepName) bits.push(escapeHtml(meta.stepName));
    if (meta.date) bits.push(escapeHtml(meta.date));
    const subtitle = bits.length
        ? `<div style="margin-top:6px;font-size:12px;color:#6b7280;">${bits.join("&nbsp;&nbsp;&bull;&nbsp;&nbsp;")}</div>`
        : "";
    const caseIdHtml = meta.caseId
        ? `<div style="margin-bottom:8px;font-size:13px;font-weight:700;color:#141bcb;letter-spacing:0.02em;">${escapeHtml(meta.caseIdLabel || "Case ID")}: ${escapeHtml(meta.caseId)}</div>`
        : "";
    return `
        <div data-print-break style="margin:0 0 8px 0;padding:0 0 14px 0;border-bottom:2px solid #141bcb;">
            ${caseIdHtml}
            <div style="font-size:22px;font-weight:700;color:#111827;line-height:1.3;">${title}</div>
            ${subtitle}
        </div>`;
};

// Renders a form definition + its values as the same section / label-value body
// used above, for callers that place it inside their own document (the request
// print lays it out in a two-column page alongside the workflow).
export const buildFormFieldsHtml = ({ formElements, values, lng = "", visibleIds = null, masterList = [] }) =>
    childrenHtml(formElements || [], printContext({ values, lng, visibleIds, masterList }), 0);

// Builds the full document as a detached, off-screen element ready for capture.
const buildStructuredPrintElement = ({ formElements, values, meta, lng, visibleIds, masterList }) => {
    const root = document.createElement("div");
    root.style.cssText = [
        "position:fixed",
        "left:-10000px",
        "top:0",
        "width:820px",
        "background:#ffffff",
        // Side padding only: it becomes the document's left and right margins once
        // the capture is scaled to the page width. The space above and below is the
        // PDF's own page margin (see formPrint.js) - carrying vertical padding here
        // too would stack the two, pushing the first line a fifth of the way down
        // the opening page.
        "padding:0 44px",
        "box-sizing:border-box",
        "font-family:Arial,Helvetica,sans-serif",
        "color:#111827",
    ].join(";");

    const body = childrenHtml(formElements || [], printContext({ values, lng, visibleIds, masterList }), 0);
    root.innerHTML = headerHtml(meta) + (body.trim() || `<div style="padding:24px 0;color:#c7ccd1;">No form data was captured.</div>`);
    return root;
};

// Public entry point: render the structured document and return it as a PDF File.
export const generateStructuredFormPrintFile = async ({
    formElements,
    values,
    meta = {},
    lng = "",
    visibleIds = null,
    masterList = [],
    fileName,
}) => {
    const element = buildStructuredPrintElement({ formElements, values, meta, lng, visibleIds, masterList });
    document.body.appendChild(element);
    try {
        return await rasterizeElementToPdfFile(element, fileName);
    } finally {
        document.body.removeChild(element);
    }
};
